import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import {
  ArrowLeft, Loader2, RefreshCw, CheckCircle2, XCircle, FileText,
  AlertTriangle, Award, ExternalLink, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-205 — Admin Certification Review Queue.
 *
 * Pending tab — talent submissions awaiting review.
 * Rejected tab — items that were rejected (re-upload follow-up).
 *
 * Approve flow is one-click.
 * Reject flow opens a modal with the canonical rejection-reason
 * dropdown (sourced live from /admin/rejection-reasons so the UI
 * never drifts from the backend's accepted values) + optional admin
 * notes. On submit the talent gets an in-app notification with the
 * reason + admin note + a deep link back to /talent/certifications.
 */

const TAB_TO_STATUS = { pending: 'pending', rejected: 'rejected' };

const isExpired = (iso) => {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch (_) { return false; }
};

const CertReviewQueue = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, rejected: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/certifications/admin/pending', {
        params: { status: TAB_TO_STATUS[tab] },
      });
      setItems(Array.isArray(res?.data?.pending_certifications) ? res.data.pending_certifications : []);
      setCounts(res?.data?.counts || { pending: 0, rejected: 0, approved: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load certifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/certifications/admin/rejection-reasons')
      .then((r) => setRejectionReasons(r?.data?.reasons || []))
      .catch(() => setRejectionReasons([
        'Expired certification', 'Wrong document uploaded',
        'Name does not match profile', 'Document is blurry/unreadable',
        'Missing required information', 'Certification type not accepted',
        'Duplicate upload', 'Other',
      ]));
  }, []);

  const approve = async (cert) => {
    try {
      await api.put(`/certifications/admin/${cert.id}/review`, { status: 'approved' });
      toast.success(`${cert.type_name} approved for ${cert.talent_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    }
  };

  const openReject = (cert) => {
    setRejectTarget(cert);
    setRejectReason('');
    setRejectNotes('');
  };

  const submitReject = async () => {
    if (!rejectTarget || !rejectReason) {
      toast.error('Select a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/certifications/admin/${rejectTarget.id}/review`, {
        status: 'rejected',
        rejection_reason: rejectReason,
        notes: rejectNotes || null,
      });
      toast.success(`Certification rejected — ${rejectTarget.talent_name} notified.`);
      setRejectTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setSubmitting(false);
    }
  };

  const viewDoc = async (cert) => {
    try {
      const res = await api.get(`/certifications/document/${cert.id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 403 ? 'Not authorized to view this document' : 'Could not load document');
    }
  };

  const refresh = () => { setRefreshing(true); load(); };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link to="/admin/home" className="text-gray-500 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-600" />
              Certification Review
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="cert-review-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="cert-tab-pending">
              Pending <Badge variant="outline" className="ml-2">{counts.pending}</Badge>
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="cert-tab-rejected">
              Rejected <Badge variant="outline" className="ml-2">{counts.rejected}</Badge>
            </TabsTrigger>
          </TabsList>

          {['pending', 'rejected'].map((value) => (
            <TabsContent key={value} value={value}>
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="py-16 flex items-center justify-center text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                  ) : items.length === 0 ? (
                    <div className="py-16 text-center text-gray-500 text-sm" data-testid={`cert-${value}-empty`}>
                      No {value} certifications.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {items.map((c) => {
                        const expired = isExpired(c.expiration_date);
                        return (
                          <li key={c.id} className="p-5" data-testid={`cert-row-${c.id}`}>
                            <div className="flex items-start gap-4 flex-wrap">
                              <Link
                                to={`/admin/talent/${c.user_id}`}
                                className="shrink-0 w-12 h-12 rounded-full bg-gray-200 overflow-hidden inline-flex items-center justify-center"
                              >
                                {c.profile_photo_url ? (
                                  <img src={c.profile_photo_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <UserIcon className="w-5 h-5 text-gray-500" />
                                )}
                              </Link>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Link
                                    to={`/admin/talent/${c.user_id}`}
                                    className="font-semibold text-gray-900 hover:text-cyan-700 hover:underline"
                                  >
                                    {c.talent_name}
                                  </Link>
                                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                                    {c.type_name}
                                  </Badge>
                                  {expired && (
                                    <Badge className="bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                      <AlertTriangle className="w-3 h-3" /> Expired
                                    </Badge>
                                  )}
                                  {value === 'rejected' && (
                                    <Badge className="bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                      <XCircle className="w-3 h-3" /> Rejected
                                    </Badge>
                                  )}
                                  {c.is_resubmitted && (
                                    <Badge
                                      className="bg-cyan-50 text-cyan-700 border border-cyan-200 inline-flex items-center gap-1"
                                      data-testid={`resubmitted-badge-${c.id}`}
                                    >
                                      <RefreshCw className="w-3 h-3" /> Resubmitted
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">{c.talent_email}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs">
                                  <div>
                                    <span className="text-gray-500">Submitted: </span>
                                    <span className="text-gray-800">{formatDate(c.uploaded_at)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Expires: </span>
                                    <span className={expired ? 'text-rose-700 font-medium' : 'text-gray-800'}>
                                      {formatDate(c.expiration_date)}
                                    </span>
                                  </div>
                                  {value === 'rejected' && c.rejection_reason && (
                                    <div className="sm:col-span-3">
                                      <span className="text-gray-500">Last reason: </span>
                                      <span className="text-rose-700">{c.rejection_reason}</span>
                                      {c.notes && <span className="text-gray-500"> · note: <span className="text-gray-700 italic">{c.notes}</span></span>}
                                    </div>
                                  )}
                                  {c.is_resubmitted && c.previous_rejection_reason && (
                                    <div className="sm:col-span-3 rounded-md bg-cyan-50 border border-cyan-200 p-2">
                                      <div className="text-cyan-900 font-medium">Previously rejected for:</div>
                                      <div className="text-cyan-800">{c.previous_rejection_reason}</div>
                                      {c.previous_admin_notes && (
                                        <div className="text-cyan-700 italic mt-0.5">
                                          Admin note: {c.previous_admin_notes}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => viewDoc(c)}
                                  data-testid={`cert-view-btn-${c.id}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5 mr-1" />
                                  View
                                </Button>
                                {value === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => approve(c)}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      data-testid={`cert-approve-btn-${c.id}`}
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openReject(c)}
                                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                                      data-testid={`cert-reject-btn-${c.id}`}
                                    >
                                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) setRejectTarget(null); }}>
        <DialogContent data-testid="cert-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-600" />
              Reject certification
            </DialogTitle>
          </DialogHeader>
          {rejectTarget && (
            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                Rejecting <span className="font-semibold">{rejectTarget.type_name}</span> for
                <span className="font-semibold"> {rejectTarget.talent_name}</span>. They'll be
                notified with the reason + your note and prompted to re-upload.
              </div>
              <div>
                <label className="text-xs text-gray-600 uppercase tracking-wide font-medium">
                  Rejection reason <span className="text-rose-600">*</span>
                </label>
                <Select value={rejectReason} onValueChange={setRejectReason}>
                  <SelectTrigger data-testid="cert-reject-reason-select">
                    <SelectValue placeholder="Choose a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {rejectionReasons.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-600 uppercase tracking-wide font-medium">
                  Admin note <span className="text-gray-400">(optional, shown to talent)</span>
                </label>
                <Textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="e.g. The back of your ID is missing. Please re-upload both sides."
                  rows={3}
                  data-testid="cert-reject-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={!rejectReason || submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="cert-reject-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
              Reject & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertReviewQueue;
